import type { CreateBankAddressFormValues } from '../model';
import { type BankAddressField } from '@/entities/bank-address';
import {
  SelectInput,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Input,
  CountrySelectInput,
} from '@/shared/components';
import type { UseFormReturn } from 'react-hook-form';
import { getRequiredLabel, useToast } from '@/shared/lib';
import { useTranslation } from 'react-i18next';
import { formatValue } from '@/features/withdraw-bank-transfer';
import { useCountriesDictionary } from '@/shared/api';

type Props = {
  form: UseFormReturn<CreateBankAddressFormValues>;
  fields: BankAddressField[];
  isLastStep: boolean;
};

export const CreateBankAddressForm = ({ form, fields, isLastStep }: Props) => {
  const { t } = useTranslation();
  const { toast, setPosition } = useToast();

  const { countriesData } = useCountriesDictionary();

  const handleEditToast = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const message = t('bankAddress.editToastMessage');
    setPosition('top-center');
    toast.dismiss();
    toast.default(message);
  };

  return (
    <Form {...form}>
      <form
        className="flex flex-col gap-[1.6rem]"
        onSubmit={e => e.preventDefault()}>
        {fields.map(fieldData => {
          if (!fieldData.visible) {
            return null;
          }

          const isCountrySelect =
            fieldData.id === 'bankCountry' &&
            fieldData.valueDictionaryId === 'countries';
          const isDisabled = fieldData.disabled;
          const placeholder = fieldData.placeholder;

          return (
            <FormField
              key={fieldData.id}
              control={form.control}
              name={fieldData.id}
              render={({ field, fieldState }) => {
                const fieldValue =
                  (field.value as string) || fieldData.defaultValue || '';

                return (
                  <FormItem>
                    <FormControl>
                      <div
                        onClick={
                          fieldData.isPrevStepParam
                            ? handleEditToast
                            : undefined
                        }>
                        {isCountrySelect ? (
                          <CountrySelectInput
                            {...field}
                            onValueChange={value => {
                              field.onChange(value);
                            }}
                            placeholder={placeholder}
                            countries={countriesData?.value ?? []}
                            disabled={isDisabled}
                            value={fieldValue}
                            error={fieldState.error}
                          />
                        ) : fieldData.type === 'select' ? (
                          <SelectInput
                            {...field}
                            value={fieldValue}
                            triggerClassName={
                              isDisabled ? 'pointer-events-none' : ''
                            }
                            floatingLabel={placeholder}
                            options={fieldData.options || []}
                            disabled={isDisabled}
                            valueKey="code"
                            selectedKey="code"
                            error={fieldState.error}
                          />
                        ) : (
                          <Input
                            {...field}
                            onChange={e => {
                              field.onChange(
                                formatValue(
                                  e.target.value,
                                  fieldData?.inputFormat
                                )
                              );
                            }}
                            aria-invalid={!!fieldState.error}
                            className={isDisabled ? 'pointer-events-none' : ''}
                            disabled={isDisabled}
                            label={placeholder}
                            value={fieldValue}
                          />
                        )}
                      </div>
                    </FormControl>
                    <FormMessage className="px-[1.6rem]" />
                  </FormItem>
                );
              }}
            />
          );
        })}

        {isLastStep && (
          <FormField
            control={form.control}
            name="templateName"
            render={({ field }) => {
              return (
                <FormItem>
                  <FormControl>
                    <Input
                      {...field}
                      label={getRequiredLabel(
                        t(
                          'savedAddresses.cryptoAddress.fields.templateNameLabel'
                        )
                      )}
                      className="p3"
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage className="px-[1.6rem]" />
                </FormItem>
              );
            }}
          />
        )}
      </form>
    </Form>
  );
};
